// The three Inbox buttons in the conversation header (owner, 2026-10-07).
//
// « ✓ All handled — E · ✗ Ignore — # · ⏰ Snooze — S. Same meaning as the
// buttons on the dashboard — reuse them. » So this calls the very same
// `pilotageApi.inboxAct` the Inbox calls, on the same items: the CLIENT's open
// Inbox items (groupByClient — the chat and its order together), exactly what
// the Inbox row for this client would act on.
//
// After any of the three: Undo for 5 s, then the next chat that needs a person
// opens. Keys never fire while typing (shortcutFor returns null in a field).
// A phone gets the same three as a bottom bar.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useT } from '@/lib/i18n';
import { useCan } from '@/lib/actions';
import { pilotageApi, type InboxItem } from '@/lib/api';
import { usePilotage } from './usePilotage';
import { groupByClient, pickedSnoozeIso, shortcutFor, snoozeChoices } from './workbench';

type Action = 'handled' | 'ignored' | 'snoozed';
type Done = { label: string; items: InboxItem[]; chatId: string };

const UNDO_MS = 5000;

/** The menu behind « Snooze »: the quick choices + « pick a time ». Shared with the Inbox. */
export function SnoozeMenu({ onPick }: { onPick: (untilIso: string) => void }) {
  const { t } = useT();
  const [local, setLocal] = useState('');
  const picked = pickedSnoozeIso(local, Date.now());
  return (
    <div className="space-y-2">
      <p className="text-[11.5px] font-semibold text-text-secondary">{t('Revient dans l’Inbox à l’heure choisie — ou dès que le client écrit.')}</p>
      <div className="flex flex-wrap gap-2">
        {snoozeChoices(Date.now()).map((c) => (
          <button key={c.label} type="button" onClick={() => onPick(c.until)}
            className="h-9 px-3 rounded-lg border border-border-default bg-bg-card text-[12.5px] text-text-primary cursor-pointer hover:bg-bg-elevated focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
            {t(c.label)}
          </button>
        ))}
      </div>
      <form className="flex flex-wrap items-center gap-2" onSubmit={(e) => { e.preventDefault(); if (picked) onPick(picked); }}>
        <label className="text-[12px] text-text-secondary" htmlFor="snooze-pick">{t('Choisir l’heure')}</label>
        <input id="snooze-pick" type="datetime-local" value={local} onChange={(e) => setLocal(e.target.value)}
          className="h-9 px-2 rounded-lg border border-border-default bg-bg-card text-[12.5px] text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-500" />
        <button type="submit" disabled={!picked}
          className="h-9 px-3 rounded-lg bg-text-primary text-bg-card text-[12.5px] font-semibold cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed">
          {t('Rappeler')}
        </button>
      </form>
    </div>
  );
}

export function ChatInboxBar({ chatId, onNextChat, onReturnToChat, compact = false, stay = false }: {
  chatId: string;
  /** Opens the next chat that needs a person (null = none left). */
  onNextChat: (chatId: string | null) => void;
  /** Undo brings the manager back to the chat they acted on. */
  onReturnToChat: (chatId: string) => void;
  /** The phone: a bottom bar. */
  compact?: boolean;
  /** On the order page: stay on this chat after an action (no « next chat »). */
  stay?: boolean;
}) {
  const { t } = useT();
  const can = useCan();
  const qc = useQueryClient();
  const { data } = usePilotage();
  const [gone, setGone] = useState<Set<string>>(() => new Set());
  const [panel, setPanel] = useState<'none' | 'ignore' | 'snooze'>('none');
  const [note, setNote] = useState('');
  const [lastDone, setLastDone] = useState<Done | null>(null);
  const [error, setError] = useState<string | null>(null);
  const noteRef = useRef<HTMLInputElement>(null);
  useEffect(() => { setPanel('none'); setNote(''); setError(null); }, [chatId]);

  // The same rows the Inbox shows, in the same order (most urgent first).
  const rows = useMemo(
    () => groupByClient((data?.inbox?.items ?? []).filter((i) => !gone.has(i.key))),
    [data, gone],
  );
  const mine = rows.find((r) => r.chatId === chatId || r.items.some((i) => i.chatId === chatId)) ?? null;
  const items = mine?.items ?? [];
  const seenAt = data?.generated_at;

  const nextChat = useCallback((excludeKeys: Set<string>) => {
    const next = rows.find((r) => r.chatId && r.chatId !== chatId && !r.items.every((i) => excludeKeys.has(i.key)));
    return next?.chatId ?? null;
  }, [rows, chatId]);

  const act = useMutation({
    mutationFn: (p: { items: InboxItem[]; action: Action; note?: string; snoozeUntil?: string }) =>
      pilotageApi.inboxAct(p.items, p.action, { note: p.note, snoozeUntil: p.snoozeUntil, seenAt }),
    onMutate: (p) => {
      setError(null);
      setPanel('none');
      setNote('');
      setGone((g) => { const n = new Set(g); for (const i of p.items) n.add(i.key); return n; });
      const label = p.action === 'handled' ? t('Traité') : p.action === 'ignored' ? t('Ignoré') : t('Rappel programmé');
      setLastDone({ label, items: p.items, chatId });
    },
    onSuccess: (res, p) => {
      if (p.action !== 'handled' || !res?.resumed?.length) return;
      setLastDone((d) => (d && d.items === p.items ? { ...d, label: t('Traité — l’IA reprend') } : d));
      void qc.invalidateQueries({ queryKey: ['whatsapp-conversations'] });
      void qc.invalidateQueries({ queryKey: ['whatsapp-conversation'] });
    },
    onError: (e, p) => {
      setGone((g) => { const n = new Set(g); for (const i of p.items) n.delete(i.key); return n; });
      setLastDone(null);
      setError((e as Error).message);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['pilotage'] }),
  });

  const undo = useMutation({
    mutationFn: async (d: Done) => { for (const i of d.items) await pilotageApi.inboxUndo(i.key); },
    onMutate: (d) => {
      setGone((g) => { const n = new Set(g); for (const i of d.items) n.delete(i.key); return n; });
      setLastDone(null);
      if (d.chatId !== chatId) onReturnToChat(d.chatId);
    },
    onError: (e) => setError((e as Error).message),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['whatsapp-conversations'] });
      void qc.invalidateQueries({ queryKey: ['whatsapp-conversation'] });
      return qc.invalidateQueries({ queryKey: ['pilotage'] });
    },
  });

  // Undo for 5 s, then the next chat opens. The clock starts once per action:
  // a background refresh of the rows must not restart it (refs, not deps).
  const nextRef = useRef({ nextChat, onNextChat, chatId, stay });
  nextRef.current = { nextChat, onNextChat, chatId, stay };
  useEffect(() => {
    if (!lastDone) return;
    const done = lastDone;
    const id = window.setTimeout(() => {
      setLastDone(null);
      const cur = nextRef.current;
      if (done.chatId === cur.chatId && !cur.stay) cur.onNextChat(cur.nextChat(new Set(done.items.map((i) => i.key))));
    }, UNDO_MS);
    return () => window.clearTimeout(id);
  }, [lastDone]);

  const has = items.length > 0;
  const run = useCallback((action: Action, extra: { note?: string; snoozeUntil?: string } = {}) => {
    if (!has || act.isPending) return;
    act.mutate({ items, action, ...extra });
  }, [has, items, act]);
  const openIgnore = useCallback(() => {
    if (!has || !can('inbox.ignore')) return;
    setPanel('ignore');
    window.setTimeout(() => noteRef.current?.focus(), 0);
  }, [has, can]);

  // Keys: E · # · S · Z (undo) · Esc. Never while typing.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const cut = shortcutFor({
        key: e.key, shiftKey: e.shiftKey, metaKey: e.metaKey, ctrlKey: e.ctrlKey, altKey: e.altKey,
        targetTag: target?.tagName ?? 'BODY', editable: Boolean(target?.isContentEditable),
      });
      if (!cut) return;
      if (cut === 'handle' && can('inbox.handle') && has) { e.preventDefault(); run('handled'); }
      else if (cut === 'ignore' && has) { e.preventDefault(); openIgnore(); }
      else if (cut === 'snooze' && has && can('inbox.snooze')) { e.preventDefault(); setPanel((p) => (p === 'snooze' ? 'none' : 'snooze')); }
      else if (cut === 'undo' && lastDone && can('inbox.undo')) { e.preventDefault(); undo.mutate(lastDone); }
      else if (cut === 'close' && panel !== 'none') setPanel('none');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [can, has, run, openIgnore, lastDone, undo, panel]);

  const btn = 'inline-flex items-center justify-center gap-1.5 rounded-lg border text-[12.5px] font-medium cursor-pointer transition-colors duration-150 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500';
  const size = compact ? 'h-11 flex-1 px-2' : 'h-9 px-3';
  const kbd = (k: string) => (compact ? null : <kbd className="ml-0.5 text-[10px] opacity-60 font-sans">{k}</kbd>);
  const why = has ? undefined : t('Rien à traiter pour cette conversation.');

  return (
    <div data-chat-inbox-bar className={compact
      ? 'relative shrink-0 border-t border-[var(--wa-header-line)] bg-[var(--wa-bar)] px-2 pt-2'
      : 'relative'}
      style={compact ? { paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom, 0px))' } : undefined}>
      {lastDone ? (
        <div role="status" className={`flex items-center gap-3 rounded-lg bg-text-primary text-bg-card px-3 ${compact ? 'h-11' : 'h-9'} text-[12.5px]`}>
          <span className="truncate">{stay ? lastDone.label : `${lastDone.label} · ${t('la conversation suivante s’ouvre…')}`}</span>
          {can('inbox.undo') && (
            <button type="button" onClick={() => undo.mutate(lastDone)} className="ml-auto shrink-0 font-semibold underline underline-offset-2 cursor-pointer">
              {t('Annuler')} {kbd('Z')}
            </button>
          )}
        </div>
      ) : (
        <div className={`flex items-center gap-1.5 ${compact ? '' : 'flex-wrap justify-end'}`}>
          {!compact && has && (
            <span className="text-[11.5px] text-text-tertiary tabular-nums mr-1">{t('{n} à traiter', { n: items.length })}</span>
          )}
          <button type="button" title={why} disabled={!has || !can('inbox.handle')} onClick={() => run('handled')}
            className={`${btn} ${size} border-success/40 bg-success/10 text-[#56654C] dark:text-status-done hover:bg-success/20`}>
            <span aria-hidden>✓</span> {t('Tout traité')} {kbd('E')}
          </button>
          <button type="button" title={why} disabled={!has || !can('inbox.ignore')} onClick={() => (panel === 'ignore' ? setPanel('none') : openIgnore())}
            aria-expanded={panel === 'ignore'}
            className={`${btn} ${size} border-border-default bg-bg-card text-text-secondary hover:bg-bg-elevated`}>
            <span aria-hidden>✗</span> {t('Ignorer')} {kbd('#')}
          </button>
          <button type="button" title={why} disabled={!has || !can('inbox.snooze')} onClick={() => setPanel(panel === 'snooze' ? 'none' : 'snooze')}
            aria-expanded={panel === 'snooze'}
            className={`${btn} ${size} border-border-default bg-bg-card text-text-secondary hover:bg-bg-elevated`}>
            <span aria-hidden>⏰</span> {t('Rappeler')} {kbd('S')}
          </button>
        </div>
      )}
      {error && <p role="alert" className="mt-1 text-[11.5px] text-error">{error}</p>}
      {panel !== 'none' && (
        // ⚠️ A MENU FLOATS, IT NEVER PUSHES (owner, 2026-10-07): on a phone the
        // ignore / snooze menu opens OVER the chat, above the bar — the
        // conversation keeps its height.
        <div className={`${compact ? 'absolute left-2 right-2 bottom-full mb-2 z-30 max-h-[60vh] overflow-y-auto shadow-xl' : 'absolute right-0 top-full mt-1.5 z-20 w-[min(360px,90vw)] shadow-lg'} rounded-lg border border-border-subtle bg-bg-card p-3`}>
          {panel === 'ignore' ? (
            <form className="space-y-2" onSubmit={(e) => { e.preventDefault(); run('ignored', { note }); }}>
              <label htmlFor="chat-ignore-note" className="block text-[11.5px] font-semibold text-text-secondary">
                {t('Pourquoi ? (facultatif — sert à créer des règles)')}
              </label>
              <input id="chat-ignore-note" ref={noteRef} value={note} onChange={(e) => setNote(e.target.value)} maxLength={500}
                placeholder={t('Ex. pas un client, message automatique…')}
                className="w-full h-10 px-3 rounded-lg border border-border-default bg-bg-card text-[13px] text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-500" />
              <div className="flex gap-2">
                <button type="submit" className="h-9 px-3.5 rounded-lg bg-text-primary text-bg-card text-[12.5px] font-semibold cursor-pointer">
                  {items.length > 1 ? t('Ignorer les {n}', { n: items.length }) : t('Ignorer cet élément')}
                </button>
                <button type="button" onClick={() => setPanel('none')} className="h-9 px-3 text-[12.5px] text-text-secondary cursor-pointer">{t('Annuler')}</button>
              </div>
            </form>
          ) : (
            <SnoozeMenu onPick={(until) => run('snoozed', { snoozeUntil: until })} />
          )}
        </div>
      )}
    </div>
  );
}
